/**
 * API Client with Axios
 * Automatically adds Clerk token to requests
 */
import axios from 'axios';

/**
 * Server-side / build: optional external API (e.g. Python on Railway).
 * Browser: always use same-origin `/api` so App Router handlers read Supabase with the
 * service role — same DB the worker writes to. If `NEXT_PUBLIC_API_URL` points the
 * browser at a different host, questions/insights can show empty while rows exist in
 * Supabase (split-brain between FastAPI DB session and Supabase).
 */
const resolvedBaseUrl =
  (process.env.NEXT_PUBLIC_API_URL && process.env.NEXT_PUBLIC_API_URL.trim()) ||
  '/api';

// Store the getToken function reference
let getTokenFunction: (() => Promise<string | null>) | null = null;

const api = axios.create({
  baseURL: resolvedBaseUrl,
  timeout: 30000, // 30 second timeout - prevents hanging requests
});

// Create a separate instance for long-running operations (like bucket scanning)
export const apiLongTimeout = axios.create({
  baseURL: resolvedBaseUrl,
  timeout: 300000, // 5 minute timeout for bucket scanning
});

function applySameOriginApiInBrowser() {
  if (typeof window === 'undefined') return;
  const force = '/api';
  api.defaults.baseURL = force;
  apiLongTimeout.defaults.baseURL = force;
}

applySameOriginApiInBrowser();

api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    config.baseURL = '/api';
  }
  return config;
});

apiLongTimeout.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    config.baseURL = '/api';
  }
  return config;
});

// Apply same interceptors to long timeout instance
apiLongTimeout.interceptors.request.use(async (config) => {
  if (typeof window !== 'undefined' && getTokenFunction) {
    try {
      const token = await getTokenFunction();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.log('No Clerk token available');
    }
  }
  return config;
});

apiLongTimeout.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Function to set the getToken function from Clerk
export const setClerkTokenGetter = (getToken: () => Promise<string | null>) => {
  getTokenFunction = getToken;
};

// Add auth token to every request
api.interceptors.request.use(async (config) => {
  if (typeof window !== 'undefined' && getTokenFunction) {
    try {
      const token = await getTokenFunction();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.log('No Clerk token available');
    }
  }
  return config;
});

// Handle 401 errors (redirect to login)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;


